import React, { useMemo, useState } from "react";
import { useLocation } from "wouter";
import { CircleDot, FlaskConical, GitCommitHorizontal, Pencil, Plus, Rocket, Trash2, Undo2 } from "lucide-react";
import { api, invalidate, timeAgo, toast, toastError, useQuery, useStatus } from "../lib";
import { Badge, Button, Dot, Empty, Field, Modal, ModelPicker, Seg, channelTone, cx } from "../ui";
import { DiffView } from "../editor";
import { EvalList } from "./project";
import { useDeploy } from "./deploy";
import { CasesSection } from "./cases";
import type { TargetData } from "./target";
import type { Grader, GraderType, Spec } from "../../shared/types";

// ---------- versões ----------

function specToText(s: Spec | undefined): string {
  if (!s) return "";
  const schema = s.output.schema ? ["", "── schema ──", JSON.stringify(s.output.schema, null, 2)] : [];
  if (s.kind === "prompt") {
    const params = Object.entries(s.params ?? {}).filter(([, v]) => v !== undefined && v !== "").map(([k, v]) => k + "=" + v).join(" ");
    const vars = Object.entries(s.variables).map(([k, v]) => k + " (" + v.type + ")").join(", ");
    return ["modelo: " + s.model, "parâmetros: " + (params || "padrão"), "saída: " + s.output.format, "variáveis: " + (vars || "—"), "", "── sistema ──", s.system, "", "── mensagem ──", s.prompt, ...schema].join("\n");
  }
  return [
    "runtime: " + s.runtime,
    "modelo: " + s.model,
    "modo: " + s.mode,
    "limites: " + s.limits.maxTurns + " turnos, " + s.limits.timeoutSec + "s",
    "sandbox: " + s.sandbox,
    "saída: " + s.output.format,
    "",
    "── tarefa ──",
    s.task,
    ...schema,
  ].join("\n");
}

export function VersionsTab({ p, t, data, onRestored, onClose }: { p: string; t: string; data: TargetData; onRestored: () => void; onClose?: () => void }) {
  const path = "/api/projects/" + p + "/targets/" + t;
  const versions = data.versions;
  const draftChanged = !versions[0] || versions[0].hash !== data.draft.hash;
  const [sel, setSel] = useState<string>(draftChanged ? "draft" : String(versions[0]?.n ?? "draft"));
  const selIdx = sel === "draft" ? -1 : versions.findIndex((v) => String(v.n) === sel);
  const defaultCmp = sel === "draft" ? String(versions[0]?.n ?? "draft") : versions[selIdx + 1] ? String(versions[selIdx + 1].n) : sel;
  const [cmp, setCmp] = useState<string | null>(null);
  const against = cmp ?? defaultCmp;
  const { data: diff } = useQuery<any>(path + "/diff?a=" + against + "&b=" + sel);
  const [, nav] = useLocation();
  const chanOf = (n: number) => Object.entries(data.meta.channels as Record<string, number>).filter(([, v]) => v === n).map(([c]) => c);
  const { deploy, dialog } = useDeploy(p, t);
  const files = useMemo(() => {
    if (!diff) return [];
    const names = [...new Set([...Object.keys(diff.a.files), ...Object.keys(diff.b.files)])].sort();
    return names.map((n) => ({
      name: n,
      a: diff.a.files[n] ?? "",
      b: diff.b.files[n] ?? "",
      status: !(n in diff.a.files) ? "novo" : !(n in diff.b.files) ? "removido" : diff.a.files[n] === diff.b.files[n] ? "igual" : "alterado",
    }));
  }, [diff]);
  const selVersion = versions.find((v) => String(v.n) === sel);
  const restore = async (n: number) => {
    try {
      await api.post(path + "/versions/" + n + "/restore");
      invalidate(path);
      onRestored();
      toast("v" + n + " copiada para o draft");
      setSel("draft");
    } catch (e) {
      toastError(e);
    }
  };
  return (
    <div className="versions">
      <div className="vlist">
        {draftChanged && (
          <div className={cx("vitem", sel === "draft" && "sel", against === "draft" && sel !== "draft" && "cmp")} onClick={() => (setSel("draft"), setCmp(null))}>
            <div className="between">
              <span className="row"><CircleDot size={14} className="faint" /><span className="vnum">Draft</span></span>
              <Badge tone="violet">não salvo</Badge>
            </div>
            <div className="muted" style={{ marginTop: 5, fontSize: 12.5 }}>Mudanças desde a v{versions[0]?.n ?? 0}</div>
          </div>
        )}
        {versions.map((v) => (
          <div key={v.n} className={cx("vitem", sel === String(v.n) && "sel", against === String(v.n) && sel !== String(v.n) && "cmp")} onClick={() => (setSel(String(v.n)), setCmp(null))}>
            <div className="between">
              <span className="row"><GitCommitHorizontal size={14} className="faint" /><span className="vnum">v{v.n}</span><span className="faint mono" style={{ fontSize: 11 }}>{v.hash.slice(0, 7)}</span></span>
              <span className="row" style={{ gap: 5 }}>{chanOf(v.n).map((c) => <Badge key={c} tone={channelTone(c) as any}>{c}</Badge>)}</span>
            </div>
            <div style={{ marginTop: 5 }}>{v.message}</div>
            <div className="faint" style={{ marginTop: 3, fontSize: 12 }}>{timeAgo(v.createdAt)}</div>
          </div>
        ))}
        {!versions.length && <Empty icon={<GitCommitHorizontal size={18} />} title="Nenhuma versão ainda">No editor, use Salvar v1 para congelar o draft.</Empty>}
      </div>
      <div className="pane">
        <div className="pane-head">
          <span className="row" style={{ gap: 10 }}>
            <b>{sel === "draft" ? "Draft" : "v" + sel}</b>
            <span className="faint">comparado com</span>
            <select className="select select-sm" style={{ width: 150 }} value={against} onChange={(e) => setCmp(e.target.value)}>
              <option value="draft">draft</option>
              {versions.map((v) => <option key={v.n} value={v.n}>v{v.n}</option>)}
            </select>
          </span>
          <span className="row" style={{ gap: 6 }}>
            {selVersion && (
              <>
                <Button size="sm" variant="ghost" icon={<Pencil size={13} />} onClick={() => { nav("/p/" + p + "/t/" + t + "?v=" + selVersion.n); onClose?.(); }}>Abrir</Button>
                <Button size="sm" icon={<Undo2 size={13} />} onClick={() => restore(selVersion.n)}>Restaurar no draft</Button>
                <Button size="sm" icon={<Rocket size={13} />} onClick={() => deploy("staging", selVersion.n)}>Staging</Button>
                <Button size="sm" variant="green" icon={<Rocket size={13} />} onClick={() => deploy("production", selVersion.n)}>Produção</Button>
              </>
            )}
          </span>
        </div>
        <div className="pane-inner col" style={{ gap: 14 }}>
          {diff ? (
            <>
              <DiffView title={<span className="mono">{data.meta.kind === "agent" ? "configuração" : "prompt"}</span>} a={specToText(diff.a.spec)} b={specToText(diff.b.spec)} />
              {files.filter((f) => f.status !== "igual").map((f) => (
                <DiffView key={f.name} title={<><span className="mono">{f.name}</span><Badge tone={f.status === "novo" ? "green" : f.status === "removido" ? "red" : "amber"}>{f.status}</Badge></>} a={f.a} b={f.b} />
              ))}
              {data.meta.kind === "agent" && files.every((f) => f.status === "igual") && <div className="faint" style={{ fontSize: 12 }}>Arquivos de contexto iguais ({files.length}).</div>}
            </>
          ) : (
            <div className="skeleton" style={{ height: 200 }} />
          )}
        </div>
      </div>
      {dialog}
    </div>
  );
}

// ---------- avaliações ----------

const GRADER_TYPES: { value: GraderType; label: string; help: string }[] = [
  { value: "json_fields", label: "Campos do JSON", help: "Compara campos da saída com o esperado do caso (normaliza acentos, caixa e números)." },
  { value: "exact", label: "Igual ao esperado", help: "A saída inteira tem que ser igual ao esperado." },
  { value: "contains", label: "Contém", help: "A saída contém certos trechos (ou o esperado, se vazio)." },
  { value: "regex", label: "Regex", help: "A saída casa com uma expressão regular." },
  { value: "json_schema", label: "Formato (JSON Schema)", help: "A saída é válida contra o schema de saída do prompt." },
  { value: "judge", label: "Juiz LLM", help: "Um modelo avalia critérios de sim ou não: com raciocínio, um por chamada, ou pela Decisions API, todos numa chamada." },
  { value: "not_empty", label: "Não vazio", help: "Passa se a execução não deu erro e a saída não está vazia." },
];

const typeLabel = (t: GraderType) => GRADER_TYPES.find((x) => x.value === t)?.label ?? t;

function graderSummary(g: Grader) {
  const c = g.config ?? {};
  switch (g.type) {
    case "json_fields":
      return "campos: " + ((c.fields ?? []).join(", ") || "todos do esperado") + (c.min && c.min < 1 ? " · mínimo " + Math.round(c.min * 100) + "%" : "");
    case "contains":
      return (c.values ?? []).length ? "contém: " + c.values.join(", ") : "contém o esperado";
    case "regex":
      return "/" + c.pattern + "/" + (c.flags ?? "i");
    case "judge":
      return (c.criteria ?? []).filter((x: string) => x.trim()).map((x: string) => "“" + x + "”").join(" ") + (c.engine === "decisions" ? " · Decisions" : "") + (c.model ? " · " + c.model : "");
    case "json_schema":
      return "schema de saída do prompt";
    default:
      return GRADER_TYPES.find((x) => x.value === g.type)?.help ?? "";
  }
}

export function EvalsTab({ p, t, data }: { p: string; t: string; data: TargetData }) {
  const path = "/api/projects/" + p + "/targets/" + t;
  const meta = data.meta;
  const [editing, setEditing] = useState<Grader | null>(null);
  const [runOpen, setRunOpen] = useState(false);
  const saveGraders = async (graders: Grader[]) => {
    try {
      await api.patch(path, { graders });
      invalidate(path);
    } catch (e) {
      toastError(e);
    }
  };
  const caseCount: number = (data as any).caseCount ?? 0;
  const varNames = Object.keys((data.draft.spec as any).variables ?? {});
  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1 className="page-title">Avaliações</h1>
          <div className="page-sub">Rode versões e modelos contra os casos de teste deste {meta.kind === "agent" ? "agente" : "prompt"}. Cada resposta é cacheada pelo conteúdo, então repetir uma avaliação só paga o que mudou.</div>
        </div>
        <Button variant="primary" icon={<FlaskConical size={14} />} disabled={!caseCount} onClick={() => setRunOpen(true)}>Rodar avaliação</Button>
      </div>
      <CasesSection p={p} t={t} varNames={varNames} />
      <div className="section">
        <div className="section-head">
          <h2 className="section-title">Avaliadores</h2>
          <Button size="sm" icon={<Plus size={13} />} onClick={() => setEditing({ id: "g-" + Date.now().toString(36), name: "", type: "json_fields", config: {} })}>Adicionar</Button>
        </div>
        <div className="card">
          <div>
            {meta.graders.map((g: Grader) => (
              <div key={g.id} className="target-line" style={{ padding: "11px 18px" }}>
                <div className="grow" style={{ minWidth: 0 }}>
                  <div className="row"><b>{g.name || typeLabel(g.type)}</b><Badge tone="plain">{typeLabel(g.type)}</Badge></div>
                  <div className="faint ellipsis" style={{ fontSize: 12, marginTop: 2 }}>{graderSummary(g)}</div>
                </div>
                <Button size="xs" variant="ghost" icon={<Pencil size={12} />} onClick={() => setEditing(g)} />
                <Button size="xs" variant="ghost" icon={<Trash2 size={12} />} onClick={() => saveGraders(meta.graders.filter((x: Grader) => x.id !== g.id))} />
              </div>
            ))}
            {!meta.graders.length && <div className="card-body faint" style={{ fontSize: 12.5 }}>Sem avaliadores, cada caso só é checado quanto a erro de execução.</div>}
          </div>
        </div>
      </div>
      <div className="section">
        <div className="section-head"><h2 className="section-title">Histórico</h2></div>
        <EvalList p={p} evals={data.evals} showTarget={false} />
      </div>
      {editing && (
        <GraderModal
          grader={editing}
          onClose={() => setEditing(null)}
          onSave={(g) => {
            const exists = meta.graders.some((x: Grader) => x.id === g.id);
            saveGraders(exists ? meta.graders.map((x: Grader) => (x.id === g.id ? g : x)) : [...meta.graders, g]);
            setEditing(null);
          }}
        />
      )}
      {runOpen && <RunEvalModal p={p} t={t} data={data} count={caseCount} onClose={() => setRunOpen(false)} />}
    </div>
  );
}

function GraderModal({ grader, onClose, onSave }: { grader: Grader; onClose: () => void; onSave: (g: Grader) => void }) {
  const [g, setG] = useState<Grader>(grader);
  const c = g.config ?? {};
  const setC = (patch: any) => setG({ ...g, config: { ...c, ...patch } });
  const status = useStatus();
  const NL = String.fromCharCode(10);
  return (
    <Modal open onClose={onClose} title={grader.name ? "Editar avaliador" : "Novo avaliador"} width={600} footer={<><Button variant="ghost" onClick={onClose}>Cancelar</Button><Button variant="primary" onClick={() => onSave(g)}>Salvar</Button></>}>
      <div className="grid-2">
        <Field label="Nome"><input className="input" value={g.name} onChange={(e) => setG({ ...g, name: e.target.value })} placeholder={typeLabel(g.type)} /></Field>
        <Field label="Tipo">
          <select className="select" value={g.type} onChange={(e) => setG({ ...g, type: e.target.value as GraderType, config: {} })}>
            {GRADER_TYPES.map((x) => <option key={x.value} value={x.value}>{x.label}</option>)}
          </select>
        </Field>
      </div>
      <div className="field-hint">{GRADER_TYPES.find((x) => x.value === g.type)?.help}</div>
      {g.type === "json_fields" && (
        <div className="grid-2">
          <Field label="Campos" hint="separados por vírgula; aceita caminho (a.b)"><input className="input mono" value={(c.fields ?? []).join(", ")} onChange={(e) => setC({ fields: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} placeholder="categoria, urgencia" /></Field>
          <Field label="Mínimo para passar" hint="fração dos campos corretos"><input className="input mono" type="number" step="0.05" min="0" max="1" value={c.min ?? 1} onChange={(e) => setC({ min: Number(e.target.value) })} /></Field>
        </div>
      )}
      {g.type === "contains" && (
        <>
          <Field label="Trechos" hint="um por linha; vazio = usa o esperado do caso"><textarea className="textarea mono" rows={4} value={(c.values ?? []).join(NL)} onChange={(e) => setC({ values: e.target.value.split(NL).filter((s) => s.trim()) })} /></Field>
          <Seg value={c.mode ?? "all"} onChange={(m) => setC({ mode: m })} options={[{ value: "all", label: "todos" }, { value: "any", label: "qualquer um" }]} />
        </>
      )}
      {g.type === "regex" && (
        <div className="grid-2">
          <Field label="Padrão"><input className="input mono" value={c.pattern ?? ""} onChange={(e) => setC({ pattern: e.target.value })} /></Field>
          <Field label="Flags"><input className="input mono" value={c.flags ?? "i"} onChange={(e) => setC({ flags: e.target.value })} /></Field>
        </div>
      )}
      {g.type === "judge" && (
        <>
          <Field label="Critérios" hint="um por linha; cada um vira uma pergunta de sim ou não"><textarea className="textarea" rows={5} value={(c.criteria ?? []).join(NL)} onChange={(e) => setC({ criteria: e.target.value.split(NL) })} placeholder="A resposta não promete prazo que não está no contexto." /></Field>
          <Field
            label="Como julgar"
            hint={c.engine === "decisions" ? "Todos os critérios numa chamada à Decisions API da OpenAI, com a probabilidade de cada um. Acima de 80% passa, abaixo de 20% reprova; no meio, o modelo juiz decide e explica." : "Uma chamada por critério; o modelo explica cada veredito citando a resposta."}
          >
            <Seg value={c.engine ?? "llm"} onChange={(e) => setC({ engine: e === "llm" ? undefined : e })} options={[{ value: "llm", label: "LLM com raciocínio" }, { value: "decisions", label: "Decisions (rápido)" }]} />
          </Field>
          <Field label={c.engine === "decisions" ? "Modelo juiz para os casos em dúvida" : "Modelo juiz"} hint={"padrão: " + (status?.judgeModel ?? "")}><ModelPicker value={c.model || status?.judgeModel || ""} kinds={["text"]} onChange={(m) => setC({ model: m })} /></Field>
        </>
      )}
    </Modal>
  );
}

function RunEvalModal({ p, t, data, count, onClose }: { p: string; t: string; data: TargetData; count: number; onClose: () => void }) {
  const ch = data.meta.channels as Record<string, number>;
  const draftChanged = !data.versions[0] || data.versions[0].hash !== data.draft.hash;
  const initial = new Set<string>(Object.values(ch).map(String));
  if (!initial.size && data.versions[0]) initial.add(String(data.versions[0].n));
  if (!initial.size) initial.add("draft");
  const [versions, setVersions] = useState<Set<string>>(initial);
  const [models, setModels] = useState<string[]>([]);
  const [adding, setAdding] = useState(false);
  const [trials, setTrials] = useState(1);
  const [busy, setBusy] = useState(false);
  const [, nav] = useLocation();
  const toggle = (v: string) => {
    const s = new Set(versions);
    if (s.has(v)) s.delete(v);
    else s.add(v);
    setVersions(s);
  };
  const cells = versions.size * Math.max(1, models.length);
  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post("/api/projects/" + p + "/targets/" + t + "/evals", {
        versions: [...versions].map((v) => (v === "draft" ? "draft" : Number(v))),
        models,
        trials,
      });
      nav("/p/" + p + "/e/" + r.id);
    } catch (e) {
      toastError(e);
      setBusy(false);
    }
  };
  const options = (draftChanged ? ["draft"] : []).concat(data.versions.map((v) => String(v.n)));
  return (
    <Modal
      open
      onClose={onClose}
      title="Rodar avaliação"
      width={620}
      footer={<><span className="faint grow mono" style={{ fontSize: 12 }}>{cells} combinação(ões) × {count} casos × {trials} = {cells * count * trials} execuções</span><Button variant="ghost" onClick={onClose}>Cancelar</Button><Button variant="primary" loading={busy} disabled={!versions.size} onClick={submit}>Rodar</Button></>}
    >
      <Field label="Versões">
        <div className="row wrap" style={{ gap: 8 }}>
          {options.map((v) => {
            const chans = Object.entries(ch).filter(([, n]) => String(n) === v).map(([c]) => c);
            return (
              <button key={v} type="button" className="btn btn-sm" style={versions.has(v) ? { borderColor: "var(--g9)", background: "var(--surface-3)" } : undefined} onClick={() => toggle(v)}>
                <input type="checkbox" readOnly checked={versions.has(v)} style={{ accentColor: "white", margin: 0 }} />
                <span className="mono">{v === "draft" ? "draft" : "v" + v}</span>
                {chans.map((c) => <Dot key={c} tone={channelTone(c) as any} />)}
              </button>
            );
          })}
        </div>
      </Field>
      <Field label="Modelos" hint="Sem escolha, cada versão usa o próprio modelo. Escolhendo, todas as versões rodam em cada modelo.">
        <div className="row wrap" style={{ gap: 6 }}>
          {models.map((m) => <span key={m} className="chip">{m}<span style={{ cursor: "pointer" }} onClick={() => setModels(models.filter((x) => x !== m))}>×</span></span>)}
          {adding ? (
            <div style={{ width: 300 }}>
              <ModelPicker defaultOpen onClose={() => setAdding(false)} value={data.draft.spec.model} kinds={data.meta.kind === "agent" ? ["agent"] : ["text", "image"]} onChange={(m) => { if (!models.includes(m)) setModels([...models, m]); setAdding(false); }} />
            </div>
          ) : (
            <Button size="sm" variant="ghost" icon={<Plus size={13} />} onClick={() => setAdding(true)}>adicionar modelo</Button>
          )}
        </div>
      </Field>
      <Field label="Repetições por caso" hint="Mais de uma mostra a variância (pass@k). Cada repetição tem cache próprio.">
        <Seg value={String(trials)} onChange={(v) => setTrials(Number(v))} options={["1", "3", "5"].map((v) => ({ value: v, label: v }))} />
      </Field>
    </Modal>
  );
}
